import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type ConnectRequest from 'webapp/models-custom/connect-request';
import type ConnectSelection from 'webapp/models-custom/connect-selection';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Source from 'webapp/models-custom/source';

// the connected sources list also shows pending connect requests and selected (not yet connected) sources
type SourceListItem = Source | ConnectRequest | ConnectSelection;

enum SourceStatus {
	ACTIVE = 'Active',
	PENDING = 'Pending',
	IN_ERROR = 'Error',
	SELECTED = 'Selected',
}

enum SourceFilterId {
	DATA_SOURCE = 1,
	STATUS = 2,
	CREATOR = 3,
}

export default class FilterSortSource extends FilterSort<SourceListItem> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked override sortProp = 'created';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override settingsNamePrefix = 'sourcesFilterSettings';

	override saveFilterInUrlHash = true;

	override searchProps: SearchProps = {
		title: 3,
		'connection.name': 1,
		'sourceType.name': 1,
	};

	override itemDomSelector = '.table-row:not(.empty)';

	constructor(options: FilterSortSettings<SourceListItem>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		this.createFilter<string>({
			id: SourceFilterId.DATA_SOURCE,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[SourceFilterId.DATA_SOURCE] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedType) => item.sourceType?.key === selectedType);
			},
		});

		// Creator
		this.createFilter<number>({
			id: SourceFilterId.CREATOR,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[SourceFilterId.CREATOR] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.creatorOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedId) => {
					if (selectedId === -1) {
						return !item.isMine;
					} else {
						return item.createdById === selectedId;
					}
				});
			},
		});

		this.createFilter<SourceStatus | 'all'>({
			id: SourceFilterId.STATUS,
			label: 'Show',
			name: 'show',
			selected: selectedFilters[SourceFilterId.STATUS] as SourceStatus | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => this.statusOptions,
			filterFn: function (item, selected) {
				return selected.some((selectedStatus) => {
					switch (selectedStatus) {
						case SourceStatus.ACTIVE:
							return (
								!item.isConnectRequest &&
								!item.isConnectSelection &&
								!item.isStatusActionable &&
								!item.isDatasetInError
							);
						case SourceStatus.IN_ERROR:
							// Based on new parent/child relationship, the filtering for error status is done in the table component
							return !item.isConnectRequest && !item.isConnectSelection;
						case SourceStatus.PENDING:
							return !!item.isConnectRequest;
						case SourceStatus.SELECTED:
							return !!item.isConnectSelection;
						default:
							return true;
					}
				});
			},
		});
	}

	get isErrorFilterActive(): boolean {
		return !!this.findFilter(SourceFilterId.STATUS)?.selected.includes(SourceStatus.IN_ERROR);
	}

	get datasourceOptions(): FilterOption<string>[] {
		const sourceTypeKeysArray: string[] = [];

		this.allItems.forEach((source) => {
			if (source.sourceType) {
				sourceTypeKeysArray.push(source.sourceType.key);
			}
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeKeysArray);
		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	get statusOptions(): FilterOption<SourceStatus | 'all'>[] {
		const options = Object.values(SourceStatus).map((status) => {
			const text = status === SourceStatus.IN_ERROR ? 'In error' : status;
			return {
				id: status,
				text,
			};
		}) as FilterOption<SourceStatus | 'all'>[];
		options.unshift({ id: 'all', text: 'All' });
		return options;
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdById = item.createdById;
			if (createdById === null) {
				return; // no current user yet; only user ids are read from the counts
			}
			if (!allItemCountByCreator[createdById]) {
				allItemCountByCreator[createdById] = 0;
			}
			allItemCountByCreator[createdById]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}
}
